import { Component, OnInit, OnDestroy, signal, WritableSignal } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { MimeTypesEnum } from 'src/app/utils/enums';
import { SaveStatusEnum } from 'src/app/services/cloud-storage/enums/save-status.enum';
import { CurrentDialogEnum } from 'src/app/utils/enums/current-dialog.enum';
import { DialogOperationEnum } from 'src/app/utils/enums/dialog-operation.enum';
import { CloudFoldersEnum } from 'src/app/utils/enums/cloud-folders.enum';
import { CloudData } from 'src/app/services/cloud-storage/metadata/CloudData';
import { CloudServiceEnum } from 'src/app/services/cloud-storage/enums/cloud-service.enum';
import { CloudDlgManagerService } from 'src/app/services/cloud-storage/cloud-dlg-manager.service';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { DriveMetadata } from 'src/app/services/cloud-storage/metadata/DriveMetadata';
import { FilenameDialogComponent } from '../dialogs/filename-dialog/filename-dialog.component';
import { ICloudSettings } from 'src/app/core/interfaces';
import { CloudSettingsService } from 'src/app/services/cloud-settings.service';

@Component({
    selector: 'asg-base-file-browser',
    templateUrl: './base-file-browser.component.html',
    styleUrls: ['./base-file-browser.component.scss'],
    standalone: true
})
export class BaseFileBrowserComponent implements OnInit, OnDestroy {
  formGroup: FormGroup;
  noCloudSelected = signal(true);
  isLoading = signal(false);
  selectedItem: CloudData | null = null;
  dialogStatus: WritableSignal<SaveStatusEnum> = signal(SaveStatusEnum.Open);
  currentCloud: CloudServiceEnum;
  protected folders: Array<CloudFoldersEnum> = [CloudFoldersEnum.Recent, CloudFoldersEnum.Myfiles, CloudFoldersEnum.Favorites, CloudFoldersEnum.Shared];
  protected showAllFiles: boolean = false;
  folderListing: CloudFoldersEnum = CloudFoldersEnum.Recent;
  openingFavoriteFolder: boolean = false;
  protected fileList: CloudData[];
  protected currentCloudForNgx: string;
  protected readonly subscriptions: Subscription = new Subscription();

  get MimeTypesEnum(): typeof MimeTypesEnum {
    return MimeTypesEnum;
  }

  get SaveStatusEnum(): typeof SaveStatusEnum {
    return SaveStatusEnum;
  }

  get DialogOperationEnum(): typeof DialogOperationEnum {
    return DialogOperationEnum;
  }

  protected get CurrentDialogEnum(): typeof CurrentDialogEnum {
    return CurrentDialogEnum;
  }

  protected get currentCloudParam(): { currentCloud: CloudServiceEnum } {
    return { currentCloud: this.currentCloud };
  }

  private get currentFolderId(): string {
    return this.cloudManager.currentFolderId;
  }

  constructor(
    protected cloudManager: CloudDlgManagerService,
    protected settingsService: SettingsService,
    protected formBuilder: FormBuilder,
    protected dialog: MatDialog,
    protected cloudSettingsService: CloudSettingsService,
  ) { }

  ngOnInit(): void {
    this.checkCurrentCloud();
    this.createForm();
    this.onValueChanges();

    // this.cloudManager.clearFolderHistory();
    this.openRootFolder();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onValueChanges(): void {
    this.subscriptions.add(
      this.formGroup.controls.cloudFolders.valueChanges.subscribe((e: CloudFoldersEnum) => {
        const cloudSettings: ICloudSettings = {
          cloudStorage: this.currentCloud,
          cloudFolder: e,
          recentPath: []
        };
        this.settingsService.saveSettings({ cloudSettings });

        // this.fileList = await this.cloudManager.getFiles(this.currentFolderId, this.showAllFiles, this.dialogStatus, e);
        if (!this.openingFavoriteFolder) {
          this.openRootFolder(this.dialogStatus());
        }
        this.openingFavoriteFolder = false;
      })
    );
  }

  doOperation(operation: DialogOperationEnum): void {
    if (operation === DialogOperationEnum.NewFolder) {
      this.selectedItem = null;
    }
    this.subscriptions.add(
      this.dialog
        .open(FilenameDialogComponent, {
          id: 'filenamedialog',
          data: {
            filename: this.selectedItem ? this.removeExtension(this.selectedItem.name) : '',
            mimetype: this.selectedItem?.mimeType || '',
            operation
          }
        })
        .afterClosed()
        .subscribe(async (data) => {
          if (data) {
            await this.handleFilenameChange(operation, data);
          }
        })
    );
  }

  async openFolder(mimeType: MimeTypesEnum | undefined, callFromBreadcrumbs: boolean = false): Promise<void> {
    if (!mimeType) {
      return;
    }
    
    if (mimeType === MimeTypesEnum.Folder || mimeType === MimeTypesEnum.SharedDrive) {
      if (!callFromBreadcrumbs && this.selectedItem) {
        this.cloudManager.addfolderToHistory(this.selectedItem);
      }

      this.fileList = await this.getFileList();
    }
  }

  async openRootFolder(dialogStatus: SaveStatusEnum = SaveStatusEnum.Open): Promise<void> {
    this.cloudManager.clearFolderHistory();
    const settings = this.settingsService.settings;
    settings.cloudSettings.recentPath = [];
    this.settingsService.saveSettings(settings);

    this.isLoading.set(true);
    this.fileList = await this.cloudManager.getFiles('root', this.showAllFiles, dialogStatus, this.folderListing);
    this.isLoading.set(false);
  }

  protected async getFileList(): Promise<CloudData[]> {
    this.isLoading.set(true);
    const result = await this.cloudManager.getFiles(this.currentFolderId, this.showAllFiles, this.dialogStatus(), this.folderListing === CloudFoldersEnum.Favorites ? CloudFoldersEnum.Myfiles : this.folderListing);
    this.isLoading.set(false);
    return result;
  }

  protected checkCurrentCloud(): void {
    this.subscriptions.add(
      this.settingsService.getSettingsObservable().subscribe(settings => {
        this.folderListing = settings.cloudSettings.cloudFolder;
        this.currentCloud = settings.cloudSettings.cloudStorage;
        if (this.currentCloud === CloudServiceEnum.GoogleDrive) {
          this.currentCloudForNgx = 'GOOGLEFOLDERS';
        } else if (this.currentCloud === CloudServiceEnum.DropBox) {
          this.currentCloudForNgx = 'DROPBOXFOLDERS';
        } else if (this.currentCloud === CloudServiceEnum.OneDrive) {
          this.currentCloudForNgx = 'ONEDRIVEFOLDERS';
        }

        if (this.currentCloud !== CloudServiceEnum.CloudStorage) {
          const isConnected = this.cloudSettingsService.isCloudConnected(this.currentCloud);
          this.noCloudSelected.set(!isConnected);
        } else {
          this.noCloudSelected.set(true);
        }
      })
    );

  }

  protected createForm(): void {
    this.formGroup = this.formBuilder.group({
      cloudFolders: [this.folderListing]
    });
  }

  protected toggleActive(e: Event): void {
    this.removeActive();

    const parent = (e.target as HTMLElement).parentElement;

    if (parent) {
      parent.id = 'cb-tableRowActive';
    }
  }

  protected removeActive(): void {
    const el = document.getElementById('cb-tableRowActive');

    if (el !== null) {
      el.id = '';
    }
  }

  private async handleFilenameChange(operation: DialogOperationEnum, fileName: string): Promise<void> {
    if (this.selectedItem && operation === DialogOperationEnum.Rename) {
      fileName += this.selectedItem.extension; // re-add extension   that's omitted in filename dialog (to avoid users messing up)
      await this.updateFile(this.selectedItem, { name: fileName });
    } else if (operation === DialogOperationEnum.NewFolder) {
      await this.cloudManager.newFolder(fileName, this.cloudManager.currentFolder);
    }

    this.fileList = await this.getFileList();
  }

  private async updateFile(file: CloudData, params: Partial<DriveMetadata>): Promise<void> {
    await this.cloudManager.updateFile(file, params);
  }

  private removeExtension(fileName: string): string {
    return fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
  }
}
